/**
 * SkyState 状态机(方案 B2)——本方案的心脏。
 * 天空驱动一切:每帧算当前 SkyState,派生色(剪影/rim/云/星)下发各层。
 * 任何图层都不许持有写死颜色,一律 pull 自这里。
 */
import { P, mix, darken, lighten, adjust, saturateUp } from './Palette';
import { clock } from './Ticker';

/** 岛面海拔线在天空渐变中的垂直位置(= Stage.GROUND_Y / LH)。
 *  此处复制常量而非 import,是为了避免 SkyRenderer ↔ Stage 循环依赖。 */
const GROUND_F = 0.62;

/**
 * 天空三段渐变的垂直停靠位置。
 *
 * 原实现是均匀的 [0, 0.5, 1] —— 意味着最亮的 bottom 落在画面最底部,
 * 而那里全被云海和岛的倒锥遮住了,**天空最亮的一段被浪费在看不见的地方**。
 * 结果:岛所在的 62% 高度处天空只有 #101634(亮度 8%),近黑;
 * 剪影再怎么压也压不出对比——近黑之上没有"更黑"的余量,岛于是消失。
 *
 * 改为 [0, 0.42, GROUND_F] 并在其下保持 bottom 色:
 * 最亮的一段正好落在地平线上(这也符合真实大气——越靠近地平线辉光越亮),
 * 岛的剪影因此获得足够的背景亮度可以"减"。这是让剪影法则在夜间成立的前提。
 */
export const SKY_STOPS = { top: 0, mid: 0.42, bottom: GROUND_F } as const;

export type Celestial = 'sun' | 'moon' | 'none';
export type WeatherName = 'rain' | 'storm' | 'snow' | 'aurora' | null;

export interface SkyState {
  top: number;
  mid: number;
  bottom: number;
  starAlpha: number;
  fogAlpha: number;
  ambient: number;
  celestial: Celestial;
}

// ── 昼夜锚点(北京时间)。相邻锚点线性(线性光空间)插值,全天无跳变。──
type Anchor = { h: number; s: SkyState };
const S = (top: number, mid: number, bottom: number, star: number, ambient: number, cel: Celestial): SkyState =>
  ({ top, mid, bottom, starAlpha: star, fogAlpha: 0, ambient, celestial: cel });

// ── 「灵墟从未有过正午」──────────────────────────────────────────────
// 原 §B2 的白昼(#6fa8d6/#8ec5e8/#dcedf7, ambient 1.0)在数学上摧毁本作美学:
//   1) bottom #dcedf7 饱和度仅 11% → darken 派生的 silhouette 必为灰(#889399);
//   2) ambient 1.0 的亮背景上,add 混合的光晕≈不可见,bloom 无从溢出。
// 故白昼替换为「长暮」:天光柔和、ambient 上限 0.65、bottom 饱和度 ≥35%,
// 全天保持"发光体读得出、剪影有色相"。叙事上亦成立:灵墟悬于云海之上,不见正午。
const NIGHT = S(0x070b1e, 0x0b1026, 0x1a2151, 1.0, 0.15, 'moon');
const DAWN = S(0x1a1333, 0x5c2751, 0xe8846b, 0.3, 0.45, 'none');
const LONGDUSK = S(0x24406b, 0x4a7fa8, 0x8fb8cc, 0.05, 0.65, 'sun'); // ← 原 DAY
// DUSK celestial='sun':黄昏正是看落日的时段,原 'none' 让太阳 17:00 就消失
// (用户实测"太阳没了"),落日应低垂于地平线直到暮色深沉(~19:15)才隐去。
const DUSK = S(0x2b1b4d, 0x7a2f5e, 0xff9e6b, 0.2, 0.55, 'sun');
const EVENING = S(0x0b1026, 0x141b3d, 0x2d3a8c, 0.9, 0.25, 'moon');

const ANCHORS: Anchor[] = [
  { h: 0, s: NIGHT }, { h: 4.5, s: NIGHT },
  { h: 6, s: DAWN },
  { h: 8, s: LONGDUSK }, { h: 16, s: LONGDUSK },
  { h: 18, s: DUSK },
  { h: 20.5, s: EVENING }, { h: 23.5, s: EVENING },
  { h: 24, s: NIGHT }, // wrap
];

function lerpState(a: SkyState, b: SkyState, t: number): SkyState {
  return {
    top: mix(a.top, b.top, t),
    mid: mix(a.mid, b.mid, t),
    bottom: mix(a.bottom, b.bottom, t),
    starAlpha: a.starAlpha + (b.starAlpha - a.starAlpha) * t,
    fogAlpha: a.fogAlpha + (b.fogAlpha - a.fogAlpha) * t,
    ambient: a.ambient + (b.ambient - a.ambient) * t,
    celestial: t < 0.5 ? a.celestial : b.celestial,
  };
}

function baseAt(hour: number): SkyState {
  for (let i = 0; i < ANCHORS.length - 1; i++) {
    const a = ANCHORS[i], b = ANCHORS[i + 1];
    if (hour >= a.h && hour < b.h) {
      const t = (hour - a.h) / (b.h - a.h);
      return lerpState(a.s, b.s, t);
    }
  }
  return NIGHT;
}

// ── 天气修饰(乘在基准上,权重 w 缓入)──
function applyWeather(base: SkyState, name: WeatherName, w: number): SkyState {
  if (!name || w <= 0) return base;
  const blend = (op: (c: number) => number, c: number) => mix(c, op(c), w);
  const s: SkyState = { ...base };
  if (name === 'rain') {
    const op = (c: number) => adjust(c, { satMul: 0.7, valMul: 0.85, hueShift: 5 });
    s.top = blend(op, base.top); s.mid = blend(op, base.mid); s.bottom = blend(op, base.bottom);
    s.fogAlpha = base.fogAlpha + 0.30 * w;
    s.starAlpha = base.starAlpha * (1 - 0.7 * w);
  } else if (name === 'storm') {
    const op = (c: number) => adjust(c, { satMul: 0.55, valMul: 0.65 });
    s.top = blend(op, base.top); s.mid = blend(op, base.mid); s.bottom = blend(op, base.bottom);
    s.fogAlpha = base.fogAlpha + 0.45 * w;
    s.starAlpha = base.starAlpha * (1 - w);
  } else if (name === 'snow') {
    const op = (c: number) => adjust(c, { valMul: 1.10, hueShift: -6 });
    s.top = blend(op, base.top); s.mid = blend(op, base.mid); s.bottom = blend(op, base.bottom);
    s.fogAlpha = base.fogAlpha + 0.20 * w;
  } else if (name === 'aurora') {
    s.fogAlpha = Math.max(0, base.fogAlpha - 0.05 * w);
  }
  return s;
}

class SkyRenderer {
  state: SkyState = NIGHT;
  weather: WeatherName = null;
  private wWeight = 0;
  private wDir = 0;         // +1 缓入 / -1 缓出
  private wRate = 1 / 8;

  // 覆盖态(sky_color / day_night_flip 用):把整体天空拉向自定义 state
  private override: SkyState | null = null;
  private oWeight = 0;
  private oDir = 0;
  private oRate = 1 / 8;

  update(_dt: number) {
    const dt = _dt;
    // 天气权重
    if (this.wDir !== 0) {
      this.wWeight += this.wDir * this.wRate * dt;
      if (this.wWeight >= 1) { this.wWeight = 1; this.wDir = 0; }
      if (this.wWeight <= 0) { this.wWeight = 0; this.wDir = 0; this.weather = null; }
    }
    // 覆盖权重
    if (this.oDir !== 0) {
      this.oWeight += this.oDir * this.oRate * dt;
      if (this.oWeight >= 1) { this.oWeight = 1; this.oDir = 0; }
      if (this.oWeight <= 0) { this.oWeight = 0; this.oDir = 0; this.override = null; }
    }

    let s = baseAt(clock.hour());
    s = applyWeather(s, this.weather, this.wWeight);
    if (this.override && this.oWeight > 0) s = lerpState(s, this.override, this.oWeight);
    this.state = s;
  }

  // ── 派生色(方案 B2,公式已修正)──

  /**
   * 天空渐变在垂直位置 f 处的实际颜色(0=top, 0.5=mid, 1=bottom)。
   * 派生剪影必须用这个,而不是直接用 bottom —— 见 silhouette 注释。
   */
  colorAt(f: number): number {
    const s = this.state;
    const { mid: M, bottom: B } = SKY_STOPS;
    const t = Math.max(0, Math.min(1, f));
    if (t <= M) return mix(s.top, s.mid, t / M);
    if (t <= B) return mix(s.mid, s.bottom, (t - M) / (B - M));
    return s.bottom; // 地平线以下保持 bottom 色(反正被云海遮着)
  }

  /**
   * 剪影色。原公式 `darken(bottom, 65%)` 有两个独立的致命缺陷:
   *
   * 1) **必然出灰**:darken = mix(c, #000, k) 在线性空间等比缩放三通道,**保留原饱和度**。
   *    天空底色越亮饱和度越低(旧白昼 bottom=#dcedf7 饱和度仅 11%),darken 出来必是灰
   *    (#899399,彩度 16)。→ 修正:先 saturateUp 拉回可用饱和度再压暗。
   *
   * 2) **参照系错了**:bottom 是天空渐变最亮的一段(垂直 100% 处),而岛坐在 62% 高度,
   *    那里的天空色接近 mid(暗得多)。于是"剪影"实际比它背后的天空还亮,
   *    根本不成其为剪影,只是一块颜色略不同的蓝斑。
   *    → 修正:从 colorAt(GROUND_F) 即**岛所在高度的实际天空色**派生。
   */
  get silhouette() {
    const s = this.state;
    const behind = this.colorAt(GROUND_F);
    const tinted = adjust(saturateUp(behind, 2.4, 0.62), { hueShift: -8 });
    return darken(tinted, 0.52 + 0.22 * s.ambient);
  }

  /** rim 光边:天光打在岛缘。提饱和+提亮,夜里额外借一点月色(否则夜间 rim 几乎不可见)。 */
  get rimLight() {
    const s = this.state;
    const lit = lighten(saturateUp(s.mid, 1.5, 0.6), 0.42 + 0.18 * (1 - s.ambient));
    return mix(lit, P.moon, 0.25 * (1 - s.ambient));
  }

  get cloudTint() { return mix(this.state.bottom, 0xffffff, 0.35); }
  get cloudEdge() { return this.state.ambient > 0.4 ? P.divine : P.ember; }
  get starColor() { return P.moon; }

  // ── 天气控制 ──
  pushWeather(name: WeatherName, easeSec = 8) {
    this.weather = name; this.wDir = 1; this.wRate = 1 / Math.max(0.1, easeSec);
  }
  popWeather(name: WeatherName, easeSec = 10) {
    if (this.weather === name) { this.wDir = -1; this.wRate = 1 / Math.max(0.1, easeSec); }
  }

  // ── 覆盖控制(sky_color / day_night_flip)──
  overrideTo(target: Partial<SkyState>, easeSec = 8) {
    const base = baseAt(clock.hour());
    this.override = { ...base, ...target };
    this.oDir = 1; this.oRate = 1 / Math.max(0.1, easeSec);
  }
  clearOverride(easeSec = 8) {
    this.oDir = -1; this.oRate = 1 / Math.max(0.1, easeSec);
  }
  /** 供 day_night_flip:取当前时段对侧时段的 state */
  opposite(): SkyState {
    const h = (clock.hour() + 12) % 24;
    return baseAt(h);
  }
}

export const sky = new SkyRenderer();
